import { useMemo } from 'react';
import {
  AppointmentStatusCodes,
  QueryFactory,
  VisitDto,
  VisitListDto,
} from '@lmt/shared-types';
import { getFormattedDateTime } from '@lmt/unicorn-shared/app/components/patients/common';
import { useVisitTypes } from '@lmt/unicorn-shared/app/features/dictionaries/selectors';
import { localizeVisitType } from '@lmt/unicorn-shared/app/helpers/localization/translations';

export interface VisitOption {
  id: number;
  caseId: number;
  title: string;
  start: Date;
}

export function getVisitOption(
  visit: VisitListDto | VisitDto,
  visitTitle: string,
): VisitOption {
  return {
    id: visit.id,
    caseId: visit.caseId,
    title: `${getFormattedDateTime(visit.start)} ${visitTitle}`,
    start: visit.start,
  };
}

export const useVisitOptions = (
  patientId?: number,
  enabled: boolean = true,
): VisitOption[] => {
  const visitTypes = useVisitTypes();

  const visitsQuery = QueryFactory.AppointmentEventsQuery.useSearchVisitsQuery(
    {
      patientId: patientId,
    },
    { enabled: enabled && !!patientId },
  );

  return useMemo(() => {
    if (!visitsQuery.data) {
      return [];
    }
    return (
      visitsQuery.data
        ?.filter(
          (x) =>
            x.statusId !== AppointmentStatusCodes.Canceled &&
            x.statusId !== AppointmentStatusCodes.Skipped,
        )
        .sort((a, b) => b.start.getTime() - a.start.getTime())
        .map((x) =>
          getVisitOption(x, localizeVisitType(visitTypes, x.typeId)),
        ) ?? []
    );
  }, [visitsQuery.data, visitTypes]);
};
